// Part of the landing page (split out of pages/Landing.js, 2026-09-24).
import React, { useEffect } from 'react';
import { currencySymbol } from '../../utils/currency';

// ── TrackerModal ─────────────────────────────────────────────────────────────
function TrackerModal({
  step, url, product, target, anyDrop, email, password, showPw, createAccount,
  loading, error, emailOnly,
  onClose, onSetTarget, onSetAnyDrop, onSetEmail, onSetPassword, onToggleShowPw,
  onSetCreateAccount, onConfirm, onTrack, onBack,
}) {
  useEffect(() => {
    const onKey = (e) => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', onKey);
    document.body.classList.add('modal-open');
    return () => {
      window.removeEventListener('keydown', onKey);
      document.body.classList.remove('modal-open');
    };
  }, [onClose]);

  const onScrim = (e) => { if (e.target === e.currentTarget) onClose(); };
  const prog = step <= 1 ? 0 : step - 1;
  const currency = currencySymbol(product?.currency);
  const targetFloat = target ? parseFloat(target) : 0;
  // An out-of-stock listing previews with price null: no "% off today" picks,
  // and "any drop" becomes a plain back-in-stock watch.
  const priceless = product ? product.price == null : false;

  const quickPicks = priceless
    ? [{ l: 'Just when it\'s back', v: null }]
    : product
    ? [
        { l: '-10%', v: +(product.price * 0.9).toFixed(2) },
        { l: '-20%', v: +(product.price * 0.8).toFixed(2) },
        { l: '-30%', v: +(product.price * 0.7).toFixed(2) },
        { l: 'Any drop', v: null },
      ]
    : [];

  return (
    <div className="m-scrim" onMouseDown={onScrim}>
      <div className="m-modal" onMouseDown={(e) => e.stopPropagation()}>
        <div className="m-modal-head">
          <span className="m-modal-title">
            {step === 1 && 'FETCHING PRODUCT…'}
            {step === 2 && 'STEP 1 OF 3 · CONFIRM & TARGET'}
            {step === 3 && 'STEP 2 OF 3 · ALERT EMAIL'}
            {step === 4 && 'TRACKING LIVE'}
          </span>
          <div className="m-modal-head-right">
            <div className="m-progress">
              <span className={`m-bar${prog > 1 ? ' m-bar--done' : prog === 1 ? ' m-bar--on' : ''}`} />
              <span className={`m-bar${prog > 2 ? ' m-bar--done' : prog === 2 ? ' m-bar--on' : ''}`} />
              <span className={`m-bar${prog >= 3 ? ' m-bar--on' : ''}`} />
            </div>
            <button className="m-close" onClick={onClose} aria-label="Close">&#x2715;</button>
          </div>
        </div>

        <div className="m-modal-body">
          {step === 1 && (
            <div className="m-fetching">
              <div className="m-spinner" />
              <div className="m-fetch-label">Parsing the Amazon listing…</div>
              <div className="m-fetch-url">{url}</div>
            </div>
          )}

          {step === 2 && product && (
            <>
              <div className="m-product-card">
                {product.image_url
                  ? <img src={product.image_url} alt="" className="m-product-thumb" />
                  : <div className="m-product-thumb m-product-thumb--ph">IMG</div>
                }
                <div className="m-product-info">
                  <div className="m-product-name">{product.title}</div>
                  <div className="m-product-price">
                    {priceless ? 'Currently unavailable' : `${currency}${product.price.toFixed(2)} now`}
                  </div>
                  <div className="m-product-meta">{product.asin}</div>
                </div>
              </div>

              <div className="m-field-label">Alert me when price drops below</div>
              <div className="m-target-row">
                <div className="m-prefix">{currency}</div>
                <input
                  className="m-target-input"
                  type="number"
                  value={anyDrop ? '' : target}
                  placeholder={anyDrop ? 'any drop' : '0.00'}
                  disabled={anyDrop}
                  onChange={(e) => { onSetTarget(e.target.value); onSetAnyDrop(false); }}
                  step="0.01"
                  min="0"
                />
              </div>
              <div className="m-quick-picks">
                {quickPicks.map((p, i) => {
                  const isOn = p.v === null ? anyDrop : (!anyDrop && parseFloat(target) === p.v);
                  return (
                    <button
                      key={i}
                      type="button"
                      className={`m-chip${isOn ? ' m-chip--on' : ''}`}
                      onClick={() => {
                        if (p.v === null) { onSetAnyDrop(true); onSetTarget(''); }
                        else { onSetTarget(p.v.toFixed(2)); onSetAnyDrop(false); }
                      }}
                    >{p.l}</button>
                  );
                })}
              </div>
              <div className="m-summary">
                <strong>Summary:</strong>{' '}
                {priceless && anyDrop
                  ? <>email me when it&apos;s back in stock.</>
                  : anyDrop
                  ? <>email me whenever the price drops below today&apos;s <strong>{currency}{product.price?.toFixed(2)}</strong>.</>
                  : target && priceless
                    ? <>email me when it&apos;s back in stock, and when price &#x2264; <strong>{currency}{targetFloat.toFixed(2)}</strong>.</>
                  : target
                    ? <>email me when price &#x2264; <strong>{currency}{targetFloat.toFixed(2)}</strong>.{' '}
                        {targetFloat >= product.price
                          ? "You'll get an alert right away."
                          : `That's ${currency}${(product.price - targetFloat).toFixed(2)} below today.`}
                      </>
                    : 'Set a target above or choose a quick pick.'}
              </div>
              {error && <div className="l-wiz-error">{error}</div>}
            </>
          )}

          {step === 3 && (
            <>
              {product && (
                <div className="m-product-card m-product-card--sm">
                  {product.image_url
                    ? <img src={product.image_url} alt="" className="m-product-thumb m-product-thumb--sm" />
                    : <div className="m-product-thumb m-product-thumb--ph m-product-thumb--sm">IMG</div>
                  }
                  <div className="m-product-info">
                    <div className="m-product-name m-product-name--sm">{product.title}</div>
                    <div className="m-product-price m-product-price--sm">
                      {priceless && anyDrop
                        ? 'Alert when back in stock'
                        : anyDrop
                        ? `Alert below ${currency}${product.price?.toFixed(2)} (any drop)`
                        : `Alert below ${currency}${targetFloat.toFixed(2)}`}
                    </div>
                  </div>
                </div>
              )}

              <div className="m-field-label">Where should alerts land?</div>
              <input
                className="m-email-input"
                type="email"
                placeholder="you@somewhere.com"
                value={email}
                onChange={(e) => onSetEmail(e.target.value)}
                onKeyDown={(e) => {
                  const canSubmit = email.includes('@') && (!createAccount || password.length >= 6) && !loading;
                  if (e.key === 'Enter' && canSubmit) onTrack();
                }}
                autoFocus
              />
              <label className="m-check-row">
                <input type="checkbox" checked={createAccount} onChange={(e) => onSetCreateAccount(e.target.checked)} />
                <span>
                  <strong>Also create my account</strong>
                  <span className="m-check-desc">
                    Unlocks the full dashboard — manage your watchlist, edit targets, view price history, add unlimited products.
                  </span>
                </span>
              </label>

              {createAccount && (
                <div className="m-pw-reveal">
                  <div className="m-field-label" style={{ marginTop: 16 }}>Set a password</div>
                  <div className="m-pw-row">
                    <input
                      className="m-email-input"
                      type={showPw ? 'text' : 'password'}
                      placeholder="At least 6 characters"
                      value={password}
                      onChange={(e) => onSetPassword(e.target.value)}
                      style={{ paddingRight: 60 }}
                    />
                    <button type="button" className="m-pw-toggle" onClick={onToggleShowPw}>
                      {showPw ? 'Hide' : 'Show'}
                    </button>
                  </div>
                  <div className="m-pw-hint">
                    {password.length === 0 && <span style={{ color: '#6B7280' }}>You&apos;ll use this to sign in to your dashboard.</span>}
                    {password.length > 0 && password.length < 6 && (
                      <span style={{ color: '#c0392b' }}>Password must be at least 6 characters ({6 - password.length} to go).</span>
                    )}
                    {password.length >= 6 && <span style={{ color: '#1F7A3A' }}>&#x2713; Looks good.</span>}
                  </div>
                </div>
              )}
              {error && <div className="l-wiz-error">{error}</div>}
            </>
          )}

          {step === 4 && product && (
            <div className="m-success">
              <div className="m-success-check">&#x2713;</div>
              <h3 className="m-success-h">You&apos;re tracking.</h3>
              <p className="m-success-p">
                We&apos;ll email <strong>{email}</strong> {priceless && anyDrop ? 'the moment it\'s back in stock.' : 'the moment the price hits your target.'}
                {createAccount && ' Your account is ready — sign in anytime to manage your watchlist.'}
              </p>
              <div className="m-summary-box">
                <div className="m-kv"><span className="m-k">Product</span><span>{product.title?.length > 42 ? product.title.substring(0, 42) + '…' : product.title}</span></div>
                <div className="m-kv"><span className="m-k">Current</span><span>{priceless ? 'Unavailable' : `${currency}${product.price.toFixed(2)}`}</span></div>
                <div className="m-kv"><span className="m-k">Target</span><span>{anyDrop ? (priceless ? 'back in stock' : 'any drop') : `${currency}${targetFloat.toFixed(2)}`}</span></div>
                <div className="m-kv"><span className="m-k">Re-checks</span><span>Every 24h</span></div>
              </div>
            </div>
          )}
        </div>

        <div className="m-modal-foot">
          {step === 1 && (
            <><button className="m-ghost" onClick={onClose}>Cancel</button><span className="m-hang-text">Hang tight…</span></>
          )}
          {step === 2 && (
            <><button className="m-ghost" onClick={onClose}>Cancel</button>
              <button className="l-url-btn" style={{ border: 'none' }} onClick={onConfirm}>Continue &#x2192; email</button>
            </>
          )}
          {step === 3 && (() => {
            const canSubmit = email.includes('@') && (!createAccount || password.length >= 6) && !loading;
            return (
              <><button className="m-ghost" onClick={onBack}>&#x2190; Back</button>
                <button
                  className="l-url-btn"
                  style={{ border: 'none', opacity: canSubmit ? 1 : 0.45, cursor: canSubmit ? 'pointer' : 'not-allowed' }}
                  onClick={onTrack}
                  disabled={!canSubmit}
                >{loading ? 'Setting up…' : 'Start tracking →'}</button>
              </>
            );
          })()}
          {step === 4 && emailOnly && (
            <button className="m-ghost" style={{ marginLeft: 'auto' }} onClick={onClose}>Done</button>
          )}
          {step === 4 && !emailOnly && (
            <span className="m-hang-text">Redirecting to dashboard…</span>
          )}
        </div>
      </div>
    </div>
  );
}

export { TrackerModal };
